import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { signOutSuccess } from "store/slices/authSlice";
import store from "../store";
import {
  AUTH_TOKEN,
  REFRESH_TOKEN,
  AUTH_EXPIRES_IN,
  USERNAME,
  USER_EMAIL,
} from "constants/AuthConstant";
import { notification } from "utils/notify";

const unauthorizedCode = [401];

export const toExpiryEpochMs = (value) => {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value.getTime();
  }
  const text = String(value).trim();
  if (/^\d+$/.test(text)) {
    const numeric = Number(text);
    return Number.isFinite(numeric) ? numeric : null;
  }
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : null;
};

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

const TOKEN_PAYLOAD_KEY = "authorization";

service.interceptors.request.use(
  async (config) => {
    if (config.url && config.url.startsWith('/') && !config.url.startsWith('//')) {
      // Normalize relative API paths so baseURL path segments are preserved.
      config.url = config.url.replace(/^\/+/, '');
    }

    if (config.baseURL && config.url && !config.url.startsWith('http')) {
      const normalizedBaseURL = config.baseURL.replace(/\/+$|^\s+|\s+$/g, '');
      const normalizedUrl = config.url.replace(/^\/+/, '');
      config.url = `${normalizedBaseURL}/${normalizedUrl}`;
      config.baseURL = undefined;
    }

    if (config.method === 'delete') {
      console.debug('API DELETE request:', {
        baseURL: config.baseURL,
        url: config.url,
      });
    }

    let jwtToken = localStorage.getItem(AUTH_TOKEN) || null;
    const expiryMs = toExpiryEpochMs(localStorage.getItem(AUTH_EXPIRES_IN));

    // Check if the token is about to expire (e.g., 1 minute before expiry)
    const isTokenExpired = expiryMs != null && Date.now() > expiryMs - 60000;

    if (isTokenExpired && localStorage.getItem(REFRESH_TOKEN)) {
      jwtToken = await refreshAccessToken();
    }

    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`;
    }

    return config;
  },
  (error) => {
    notification.error({ message: "Error" });
    return Promise.reject(error);
  }
);

service.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    // Check if this is an endpoint that handles its own error messages in the component
    const requestUrl = error.config?.url || '';
    const isOutboundCallSummary = requestUrl.includes('outbound-call-summary');
    const isQuickDialPreviewCamp = requestUrl.includes('quickDialPreviewCamp');
    const isContactListUpload = requestUrl.includes('uploadFileContactList');
    const isCrmApi = requestUrl.includes('/crm/');
    // Predictive Live evidence panels are best-effort; UI catches and continues.
    const isPredictiveEvidence =
      requestUrl.includes('/predictive/campaigns/') &&
      (requestUrl.includes('/decisions') ||
        requestUrl.includes('/amd-quality') ||
        requestUrl.includes('/amd-confusion') ||
        (requestUrl.includes('/close') && (error.config?.method || 'get').toLowerCase() === 'get'));

    let notificationParam = { message: "" };
    if (error.response) {
      const { data, status } = error.response;
      if (typeof data === "string" && data.trim()) {
        notificationParam.message = data;
      } else if (data?.message) {
        notificationParam.message = data.message;
      } else if (data?.detail) {
        notificationParam.message = data.detail;
      } else if (data?.status) {
        try {
          const s = data.status;
          const parsed = typeof s === "string" ? JSON.parse(s) : s;
          notificationParam.message = parsed?.status ?? s;
        } catch {
          notificationParam.message = String(data.status);
        }
      } else if (status === 409) {
        notificationParam.message = "Duplicate entry already exists.";
      } else if (status === 400) {
        notificationParam.message = "Invalid request. Please check your input.";
      } else if (status >= 500) {
        notificationParam.message = "Server error. Please try again.";
      } else if (data && typeof data === "object" && Object.keys(data).length > 0) {
        notificationParam.message = JSON.stringify(data);
      } else {
        notificationParam.message = "Something went wrong";
      }
    } else {
      notificationParam.message = "Something went wrong";
    }

    // Skip error notification for endpoints that show their own message in the component
    if (!isOutboundCallSummary && !isQuickDialPreviewCamp && !isContactListUpload && !isCrmApi && !isPredictiveEvidence) {
      notification.error(notificationParam);
    }

    // Handle specific status codes
    if (error.response && unauthorizedCode.includes(error.response.status)) {
      notificationParam.message = "Authentication Fail";
      notificationParam.description = "Please login again";
      localStorage.removeItem(AUTH_TOKEN);
      store.dispatch(signOutSuccess());
    }

    return Promise.reject(error);
  }
);
// (error) => {
//   let notificationParam = { message: "" };
  
//   // ✅ FIXED: Proper null checking with correct logical operators
//   if (error.response && error.response.data) {
//     notificationParam.message = error.response.data.message || error.response.data;
//   } else if (error.request) {
//     // Request was made but no response received
//     notificationParam.message = "No response from server. Please check your connection.";
//   } else {
//     // Something else happened
//     notificationParam.message = error.message || "Something went wrong";
//   }
  
//   notification.error(notificationParam);

//   // Handle specific status codes - with null check
//   if (error.response && unauthorizedCode.includes(error.response.status)) {
//     notificationParam.message = "Authentication Fail";
//     notificationParam.description = "Please login again";
//     localStorage.removeItem(AUTH_TOKEN);
//     store.dispatch(signOutSuccess());
//   }

//   return Promise.reject(error);
// });

const refreshAccessToken = async () => {
  try {
    if (window.refreshTokenTimer) {
      clearTimeout(window.refreshTokenTimer);
    }
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    if (refreshToken) {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/refreshtoken`,
        {
          refreshToken: refreshToken,
        }
      );

      const newAccessToken = response.data.accessToken;

      localStorage.setItem(AUTH_TOKEN, String(newAccessToken));
      localStorage.setItem(REFRESH_TOKEN, String(response.data.refreshToken));
      const expiryMs = toExpiryEpochMs(response.data.tokenExpiry);
      if (expiryMs != null) {
        localStorage.setItem(AUTH_EXPIRES_IN, String(expiryMs));
        const tokenExpirySeconds = (expiryMs - Date.now()) / 1000 - 60;
        if (Number.isFinite(tokenExpirySeconds)) {
          setRefreshTokenTimer(tokenExpirySeconds);
        }
      }
      return newAccessToken;
    }
  } catch (error) {
    console.error("Error refreshing token: ", error);
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem(USER_EMAIL);
    localStorage.removeItem(USERNAME);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(AUTH_EXPIRES_IN);
    window.location.reload();
  }
};

const setRefreshTokenTimer = (expiresIn) => {
  if (window.refreshTokenTimer) {
    clearTimeout(window.refreshTokenTimer);
  }
  const refreshTime = (expiresIn - 300) * 1000; // 5 minutes before expiry
  if (!Number.isFinite(refreshTime)) {
    return;
  }
  if (refreshTime > 0) {
    window.refreshTokenTimer = setTimeout(() => {
      refreshAccessToken();
    }, refreshTime);
  } else {
    // If the token is already expired or about to expire, refresh immediately
    refreshAccessToken();
  }
};

const initializeTokenRefresh = () => {
  const expiryMs = toExpiryEpochMs(localStorage.getItem(AUTH_EXPIRES_IN));
  if (expiryMs == null || !localStorage.getItem(REFRESH_TOKEN)) {
    return;
  }
  const currentTime = Date.now();
  if (currentTime >= expiryMs) {
    refreshAccessToken();
    return;
  }
  setRefreshTokenTimer((expiryMs - currentTime) / 1000);
};

initializeTokenRefresh();

// Download Excel function (unchanged)
const handleDownloadExcel = async (apiUrl, fileName) => {
  try {
    const response = await service({
      method: "GET",
      url: apiUrl,
      responseType: "blob",
    });
    console.log("contact data "+JSON.stringify(response))
    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log("File downloaded successfully");
  } catch (error) {
    console.error(
      "Error response data:",
      error.response ? error.response.data : "No response data"
    );
    console.error("Error downloading sample CSV:", error);
  }
};

export default service;
export { handleDownloadExcel, setRefreshTokenTimer };
